import { Box, Paper, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import Header from "./components/Header";
import ClaimTabs from "../dashboard/claims/common/ClaimTabs";
import theme from "../../theme";
import ButtonUnderwriter from "./components/ButtonUnderwriter";
import PolicyDetails from "./forms/PolicyDetails";
import ClaimantPersonalInformation from "./forms/ClaimantPersonalInformation";
import DetailsOfDeceased from "./forms/DetailsOfDeceased";
import BankingInformation from "./forms/BankingInformation";
import ClaimDocument from "./forms/ClaimDocument";
import activePolicy from "../../assets/images/activePolicy.svg";
import inactivePolicy from "../../assets/images/inactivePolicy.svg";
import activeClaim from "../../assets/images/activeClaim.svg";
import inactiveClaim from "../../assets/images/inactiveClaim.svg";
import activeDetails from "../../assets/images/activeDetails.svg";
import inactiveDetails from "../../assets/images/inactiveDetails.svg";
import activeBanking from "../../assets/images/activeBanking.svg";
import inactiveBanking from "../../assets/images/inactiveBanking.svg";
import activeClaimInfo from "../../assets/images/activeClaimInfo.svg";
import inactiveClaimInfo from "../../assets/images/inactiveClaimInfo.svg";
import checkIcon from "../../assets/images/checkIcon.svg";
import rejectIcon from "../../assets/images/rejectIcon.svg";
import VerifiedPopup from "../../components/VerifiedPopup";
import { useVerifiedStore } from "../../store/useVerifiedStore";
import RejectDialogueBox from "./components/RejectDialogueBox";
import RejectedPopup from "../../components/RejectedPopup";
import { claimSubmit, getClaimDetails } from "../../services/dashboard";
import { useParams } from "react-router-dom";
import { useUnderwriterClaimDeatilsStore } from "../../store/useUnderwriterclaimDetailsStore";
import ClaimApprovePopup from "./components/ClaimApprovePopup";
import ClaimSubmitResponse from "./components/ClaimSubmitResponse";
import { useToast } from "../../hooks/useToast";
import Loader from "../../components/Loader";

const tabData = [
  {
    id: 0,
    icon: {
      active: activePolicy,
      inactive: inactivePolicy,
    },
    alt: "Policy Information",
  },
  {
    id: 1,
    icon: {
      active: activeClaim,
      inactive: inactiveClaim,
    },
    alt: "Claimant Info",
  },
  {
    id: 2,
    icon: {
      active: activeDetails,
      inactive: inactiveDetails,
    },
    alt: "Details of Deceased",
  },
  {
    id: 3,
    icon: {
      active: activeBanking,
      inactive: inactiveBanking,
    },
    alt: "Banking Info",
  },
  {
    id: 4,
    icon: {
      active: activeClaimInfo,
      inactive: inactiveClaimInfo,
    },
    alt: "Claims Benefit",
  },
];

export default function UnderWriterDashboard() {
  const [activeTab, setActiveTab] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [reason, setReason] = useState("");
  const [policyComment, setPolicyComment] = useState("");
  const [claimantComment, setClaimantComment] = useState("");
  const [deceasedComment, setDeceasedComment] = useState("");
  const [bankingComment, setBankingComment] = useState("");
  const [benefitComment, setBenefitComment] = useState("");
  const [showClaimApprove, setShowClaimApprove] = useState(false);
  const [claimSubmitResponse, setClaimSubmitResponse] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState("");
  const { claimId } = useParams();

  const showVerified = useVerifiedStore((state) => state.showVerified);
  const setShowVerified = useVerifiedStore((state) => state.setShowVerified);
  const showRejected = useVerifiedStore((state) => state.showRejected);
  const setShowRejected = useVerifiedStore((state) => state.setShowRejected);
  const showGenerated = useVerifiedStore((state) => state.showGenerated);
  const setShowGenerated = useVerifiedStore((state) => state.setShowGenerated);

  const setUnderwriterClaimDetails = useUnderwriterClaimDeatilsStore(
    (state) => state.setUnderwriterClaimDetails
  );
  const data = useUnderwriterClaimDeatilsStore(
    (state) => state.underwriterClaimDetails
  );

  const { showInfoToast } = useToast();

  const documents = data?.documentResponseDtoList.reduce(
    (acc: Record<string, any[]>, doc: any) => {
      if (!acc[doc.document_type]) acc[doc.document_type] = [];
      acc[doc.document_type].push(doc);
      return acc;
    },
    {}
  );

  const getFiles = (value: any) => {
    setFile(value);
  };

  const getReason = (value: any) => {
    setReason(value);
  };

  const callGetClaimDetails = async (claimId: string) => {
    setIsLoading(true);
    const res = await getClaimDetails(claimId, "underwriter");
    if (res?.status === 200) {
      setUnderwriterClaimDetails(res.data);
    } else {
      showInfoToast("Could not load claim details. Please try again.");
    }
    setIsLoading(false);
  };

  const handlePolicyCommentChange = (e: any) => {
    setPolicyComment(e.target.value);
  };

  const handleClaimantCommentChange = (e: any) => {
    setClaimantComment(e.target.value);
  };

  const handleDeceasedCommentChange = (e: any) => {
    setDeceasedComment(e.target.value);
  };

  const handleBankingCommentChange = (e: any) => {
    setBankingComment(e.target.value);
  };

  const handleBenefitCommentChange = (e: any) => {
    setBenefitComment(e.target.value);
  };

  const handleSubmission = async (label: string) => {
    setShowClaimApprove(false);
    setIsLoading(true);
    const payload = {
      claim_id: claimId,
      claim_status: label === "Approve" ? "APPROVED" : "DECLINED",
      status_reason: label === "Decline" ? reason : "",
      policy_information: {
        comments: policyComment,
      },
      claimant_personal_information: {
        comments: claimantComment,
      },
      details_of_deceased: {
        comments: deceasedComment,
      },
      banking_information: {
        comments: bankingComment,
      },
      claims_benefit_schedule: {
        comments: benefitComment,
      },
    };

    const formData = new FormData();
    const payloadBlob = new Blob([JSON.stringify(payload)], {
      type: "application/json",
    });
    formData.append("claim", payloadBlob);
    if (file) {
      formData.append("file", file);
    }
    const res = await claimSubmit(formData);
    if (res?.status === 200) {
      setStatus(res.data.status);
      setClaimSubmitResponse(true);
    } else {
      showInfoToast("Could not submit claim. Please try again.");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    callGetClaimDetails(claimId as string);
  }, [claimId]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <PolicyDetails
            policyDetails={data?.policyResponseDto}
            commentDetails={data?.commentResponseDtoList}
            policyCopy={documents?.POLICY || []}
            handlePolicyCommentChange={handlePolicyCommentChange}
            policyComment={policyComment}
          />
        );
      case 1:
        return (
          <ClaimantPersonalInformation
            claimantDetails={data?.claimantResponseDto}
            commentDetails={data?.commentResponseDtoList}
            claimantCopy={documents?.CLAIMANT_ID || []}
            handleClaimantCommentChange={handleClaimantCommentChange}
            claimantComment={claimantComment}
          />
        );
      case 2:
        return (
          <DetailsOfDeceased
            deceasedDetails={data?.deceasedResponseDto}
            commentDetails={data?.commentResponseDtoList}
            DeceasedCopy={documents?.DECEASED_ID || []}
            BICopy={documents?.BI_FORM || []}
            handleDeceasedCommentChange={handleDeceasedCommentChange}
            deceasedComment={deceasedComment}
          />
        );

      case 3:
        return (
          <BankingInformation
            bankDetails={data?.bankingInfoResponseDto}
            commentDetails={data?.commentResponseDtoList}
            bankStatementCopy={documents?.BANK_STATEMENT || []}
            signatureCopy={documents?.SIGNATURE_OF_CLAIMANT || []}
            handleBankingCommentChange={handleBankingCommentChange}
            bankingComment={bankingComment}
          />
        );
      case 4:
        return (
          <ClaimDocument
            claimBenefitInfo={data?.benefitSelections}
            commentDetails={data?.commentResponseDtoList}
            handleBenefitCommentChange={handleBenefitCommentChange}
            benefitComment={benefitComment}
          />
        );
      default:
        return null;
    }
  };
  const renderTitle = () => {
    switch (activeTab) {
      case 0:
        return "Policy Details";
      case 1:
        return "Claimant Personal Information";
      case 2:
        return "Details Of Deceased";
      case 3:
        return "Banking Information";
      case 4:
        return "Claim Document";
      default:
        return null;
    }
  };
  return (
    <>
      <Header />
      {claimSubmitResponse ? (
        <ClaimSubmitResponse status={status} isLoading={isLoading} />
      ) : (
        <Box display="flex" flexDirection="column">
          {isLoading ? <Loader isLoading={isLoading} /> : null}
          <Box sx={{ px: { xs: 2, md: 26 }, mt: 5 }}>
            <ClaimTabs
              tabs={tabData}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </Box>
          <Box sx={{ px: 18 }}>
            <Paper
              sx={{
                p: 4,
                borderRadius: 2,
                background: "#ffffff",
              }}
              elevation={3}
            >
              <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography
                  fontSize={25}
                  fontWeight={600}
                  color="primary"
                  textAlign="left"
                  gutterBottom
                  textTransform="uppercase"
                >
                  {renderTitle()}
                </Typography>
              </Box>
              {renderTabContent()}
            </Paper>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={6}
              py={5}
            >
              <ButtonUnderwriter
                onClick={() => setShowClaimApprove(true)}
                icon={checkIcon}
                label="Approve"
                color="#20BF55"
              />
              <ButtonUnderwriter
                onClick={() => setShowRejected(true)}
                icon={rejectIcon}
                label="Decline"
                color="#E8003F"
              />
            </Box>
          </Box>
          <VerifiedPopup
            label="Approved"
            open={showVerified}
            onClose={() => {
              setShowVerified(false);
            }}
            text="Mail Sent"
          />
          <RejectDialogueBox
            label="Decline"
            open={showRejected}
            onClose={() => setShowRejected(false)}
            handleSubmission={handleSubmission}
            getFiles={getFiles}
            file={file}
            getReason={getReason}
            reason={reason}
          />
          <RejectedPopup
            label="Generated"
            open={showGenerated}
            onClose={() => setShowGenerated(false)}
            text="Mail Sent"
          />
          <ClaimApprovePopup
            open={showClaimApprove}
            onClose={() => setShowClaimApprove(false)}
            onApprove={handleSubmission}
          />
        </Box>
      )}
    </>
  );
}
